import { Dialog } from "@kobalte/core/dialog";
import { getIconComponent, ICON_MAPPING } from "@storytell/bsky-icons";
import { useCallOptions, useLabelClient, withBearerToken } from "@storytell/cp-client";
import { dispatchGlobalEvent } from "@storytell/events";
import { AnimateChildren } from "@storytell/ui/animation";
import { StButton } from "@storytell/ui/buttons";
import { TbPencil, TbTag, TbTrash, TbX } from "solid-icons/tb";
import { For, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { isLightColor } from "~/components/layouts/minimal/isLightColor";
import { truncateMiddle } from "~/utils/truncateMiddle";
import { useWire } from "~/wire";

export const ManageLabelsModal = () => {
  const wire = useWire();

  const labels = () => wire.sync.labels.search("");

  return (
    <AnimateChildren
      show={wire.ui.manageLabels.isModalOpen()}
      enter={{ opacity: [0, 1], y: [10, 0] }}
      exit={{ opacity: [1, 0], y: [0, 10] }}
    >
      {(anim) => (
        <Dialog
          open={anim.isMounted()}
          onOpenChange={(val) => {
            if (!val) wire.ui.manageLabels.closeModal();
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay class="fixed z-90 inset-0 bg-black/50 dark:bg-white/10" />
            <div ref={anim.ref} class="fixed z-90 inset-0 flex items-center justify-center">
              <Dialog.Content class="max-h-[80vh] relative w-screen text-primary-text max-w-xl bg-panel overflow-y-auto rounded-xl p-8">
                <Dialog.CloseButton as={StButton} class="absolute top-4 right-4">
                  <TbX />
                </Dialog.CloseButton>
                <div>
                  <h2 class="text-lg font-medium mb-5">Labels</h2>

                  <div class="mb-5 flex items-center justify-between gap-2">
                    <p class="text-secondary-text">
                      {labels().length} label{labels().length === 1 ? "" : "s"}
                    </p>
                    <StButton icon={TbTag}>Create a new Label</StButton>
                  </div>
                  <div class="flex flex-col gap-6 max-h-[60vh] overflow-y-auto">
                    <For each={labels()}>
                      {(label) => (
                        <div class="flex items-center gap-2 justify-between">
                          <div class="flex flex-col gap-1">
                            <div
                              class="flex items-center gap-2"
                              style={{
                                "--label-color": label.color,
                                "--label-icon": isLightColor(label.color) ? "black" : "white",
                              }}
                            >
                              <Dynamic
                                component={getIconComponent(label.icon)}
                                class="text-[var(--label-icon)] bg-[var(--label-color)] rounded-full size-6 p-1"
                              />
                              {truncateMiddle(label.name, 50)}
                            </div>

                            <Show when={label.description}>
                              <p class="text-xs text-secondary-text line-clamp-2 ml-8">{label.description}</p>
                            </Show>
                          </div>

                          <div class="flex items-center gap-2">
                            <StButton
                              onClick={async () => {
                                if (
                                  !window.confirm(
                                    `Are you sure you want to delete ${label.name}? It will be removed from ${wire.sync.getAssetsForLabel(label.id).length} asset${wire.sync.getAssetsForLabel(label.id).length === 1 ? "" : "s"}`,
                                  )
                                ) {
                                  return;
                                }

                                const client = useLabelClient();
                                const result = await client.deleteLabel(
                                  {
                                    labelId: label.id,
                                  },
                                  useCallOptions(withBearerToken(wire.auth.token())),
                                );

                                if (result.isOk()) {
                                  dispatchGlobalEvent("refresh-sync", undefined);
                                }
                              }}
                              icon={TbTrash}
                            />
                            <StButton icon={TbPencil} />
                          </div>
                        </div>
                      )}
                    </For>
                  </div>
                </div>
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        </Dialog>
      )}
    </AnimateChildren>
  );
};
